// See Documentation: ../../../Document/QueueInsights/hooks/use-queue-filters.md
import { useState, useEffect, useMemo, useCallback } from "react";
import { QueueSQLData, QueueFilterOptions } from "../../types/queueTypes";
import { FilterOption } from "../filters/QueueInsightsGlobalFilter";
import { ISOFilterOptions } from "../constants/queueInsightsConstants";
import { 
    filterQueueData, 
    getFilterOptions, 
    extractOptionValues 
} from "../../utils/queue-iso-utils";
import { selectedArea } from "../../types/types";

export interface UseQueueFiltersProps {
    queueData: QueueSQLData[];
    queueDataLoading: boolean;
    selectedArea?: selectedArea | null;
}

export interface UseQueueFiltersReturn {

    // Selected Filter States.
    currentISOFilter: ISOFilterOptions;
    setCurrentISOFilter: (filter: ISOFilterOptions) => void;
    selectedGenerationTypes: string[];
    setSelectedGenerationTypes: (types: string[]) => void;
    selectedStateTypes: string[];
    setSelectedStateTypes: (states: string[]) => void;
    selectedCountyTypes: string[];
    setSelectedCountyTypes: (counties: string[]) => void;
    selectedClusterTypes: string[];
    setSelectedClusterTypes: (clusters: string[]) => void;
    
    // Filter options on load.
    generationTypeOptions: FilterOption[];
    stateTypeOptions: FilterOption[];
    countyTypeOptions: FilterOption[];
    clusterTypeOptions: FilterOption[];
    isFilterOptionsLoading: boolean;
    
    // Variable for filtered data based on all current filter selections.
    filteredIsoData: QueueSQLData[];
    
    resetFilters: () => void;
    
    // Contains all the unique values for each filter option.
    allGenerationTypeValues: string[];
    allStateTypeValues: string[];
    allCountyTypeValues: string[];
    allClusterTypeValues: string[];
}

export const useQueueFilters = ({ queueData, queueDataLoading, selectedArea }: UseQueueFiltersProps): UseQueueFiltersReturn => {
    const [currentISOFilter, setCurrentISOFilter] = useState<ISOFilterOptions>(null);
    const [selectedGenerationTypes, setSelectedGenerationTypes] = useState<string[]>([]);
    const [selectedStateTypes, setSelectedStateTypes] = useState<string[]>([]);
    const [selectedCountyTypes, setSelectedCountyTypes] = useState<string[]>([]);
    const [selectedClusterTypes, setSelectedClusterTypes] = useState<string[]>([]);
    const [isFilterOptionsLoading, setIsFilterOptionsLoading] = useState(true);
    const [generationTypeOptions, setGenerationTypeOptions] = useState<FilterOption[]>([]);
    const [clusterTypeOptions, setClusterTypeOptions] = useState<FilterOption[]>([]);
    const [stateTypeOptions, setStateTypeOptions] = useState<FilterOption[]>([]);
    const [countyTypeOptions, setCountyTypeOptions] = useState<FilterOption[]>([]);

    const [initialized, setInitialized] = useState({
        generation: false,
        state: false,
        county: false,
        cluster: false,
    });

    // Check if all filters have been initialized
    const allFiltersInitialized = useMemo(() => {
        return initialized.generation && 
               initialized.state && 
               initialized.county && 
               initialized.cluster;
    }, [initialized.generation, initialized.state, initialized.county, initialized.cluster]);

    const filterOptions = useMemo(() => {
        return getFilterOptions({ options: queueData, loading: queueDataLoading }, currentISOFilter);
    }, [queueData, queueDataLoading, currentISOFilter]);

    // Create filtered data based on all current filter selections
    const filteredIsoData = useMemo(() => {
        const filters: QueueFilterOptions = {
            isoFilter: currentISOFilter,
            generationTypes: selectedGenerationTypes,
            stateTypes: selectedStateTypes,
            countyTypes: selectedCountyTypes,
            clusterTypes: selectedClusterTypes,
            selectedArea,
        };
        return filterQueueData(queueData, filters, allFiltersInitialized);
    }, [
        queueData,
        currentISOFilter,
        selectedGenerationTypes,
        selectedStateTypes,
        selectedCountyTypes,
        selectedClusterTypes,
        selectedArea,
        allFiltersInitialized,
    ]);

    // Memoize the mapped option values using utility function
    const allGenerationTypeValues = useMemo(
        () => extractOptionValues(generationTypeOptions),
        [generationTypeOptions]
    );
    const allStateTypeValues = useMemo(
        () => extractOptionValues(stateTypeOptions),
        [stateTypeOptions]
    );
    const allCountyTypeValues = useMemo(
        () => extractOptionValues(countyTypeOptions),
        [countyTypeOptions]
    );
    const allClusterTypeValues = useMemo(
        () => extractOptionValues(clusterTypeOptions),
        [clusterTypeOptions]
    );

    // Update global filters selected values when options are loaded
    useEffect(() => {
        if (!filterOptions) {
            setIsFilterOptionsLoading(true);
        } else {
            setIsFilterOptionsLoading(false);
            setGenerationTypeOptions(filterOptions.generationTypeOptions);
            setClusterTypeOptions(filterOptions.clusterTypeOptions);
            setStateTypeOptions(filterOptions.stateTypeOptions);
            setCountyTypeOptions(filterOptions.countyTypeOptions);

            // Reset initialization flags when ISO filter changes to trigger re-initialization
            setInitialized({
                generation: false,
                state: false,
                county: false,
                cluster: false,
            });
        }
    }, [filterOptions]);

    useEffect(() => {
        if (
            !isFilterOptionsLoading &&
            allGenerationTypeValues.length > 0 &&
            !initialized.generation
        ) {
            setSelectedGenerationTypes(allGenerationTypeValues);
            setInitialized(prev => ({ ...prev, generation: true }));
        }
    }, [isFilterOptionsLoading, allGenerationTypeValues, initialized.generation]);

    useEffect(() => {
        if (
            !isFilterOptionsLoading &&
            allStateTypeValues.length > 0 &&
            !initialized.state
        ) {
            setSelectedStateTypes(allStateTypeValues);
            setInitialized(prev => ({ ...prev, state: true }));
        }
    }, [isFilterOptionsLoading, allStateTypeValues, initialized.state]);

    useEffect(() => {
        if (
            !isFilterOptionsLoading &&
            allCountyTypeValues.length > 0 &&
            !initialized.county
        ) {
            setSelectedCountyTypes(allCountyTypeValues);
            setInitialized(prev => ({ ...prev, county: true }));
        }
    }, [isFilterOptionsLoading, allCountyTypeValues, initialized.county]);

    useEffect(() => {
        if (
            !isFilterOptionsLoading &&
            allClusterTypeValues.length > 0 &&
            !initialized.cluster
        ) {
            setSelectedClusterTypes(allClusterTypeValues);
            setInitialized(prev => ({ ...prev, cluster: true }));
        }
    }, [isFilterOptionsLoading, allClusterTypeValues, initialized.cluster]);

    // Memoized global filter reset function
    const resetFilters = useCallback(() => {
        setCurrentISOFilter(null);
        if (allGenerationTypeValues.length > 0) {
            setSelectedGenerationTypes(allGenerationTypeValues);
        }
        if (allStateTypeValues.length > 0) {
            setSelectedStateTypes(allStateTypeValues);
        }
        if (allCountyTypeValues.length > 0) {
            setSelectedCountyTypes(allCountyTypeValues);
        }
        if (allClusterTypeValues.length > 0) {
            setSelectedClusterTypes(allClusterTypeValues);
        }
    }, [
        allGenerationTypeValues,
        allStateTypeValues,
        allCountyTypeValues,
        allClusterTypeValues,
    ]);

    return {
        currentISOFilter,
        setCurrentISOFilter,
        selectedGenerationTypes,
        setSelectedGenerationTypes,
        selectedStateTypes,
        setSelectedStateTypes,
        selectedCountyTypes,
        setSelectedCountyTypes,
        selectedClusterTypes,
        setSelectedClusterTypes,
        generationTypeOptions,
        stateTypeOptions,
        countyTypeOptions,
        clusterTypeOptions,
        isFilterOptionsLoading,
        filteredIsoData,
        resetFilters,
        allGenerationTypeValues,
        allStateTypeValues,
        allCountyTypeValues,
        allClusterTypeValues,
    };
}; 
